/**
 * TransactionsPanel — Tab „Umsätze" (architecture-bank-reconciliation.md
 * §10.1): paginierte `bank_transactions`-Liste mit Konto-/Status-Filter,
 * Zeilen-Detail (`rawSource` einsehbar).
 *
 * @module TransactionsPanel
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevronLeft, IconChevronRight, IconView, IconChevronDown, IconChevronUp } from '../../icons';
import { ClipLoader } from 'react-spinners';
import { listTransactions, listBankAccounts } from '../../api/bankApi';
import type { BankAccount, BankTransaction, BankTxMatchStatus } from '../../types/bankTypes';
import { centsToEuros } from '../../utils/moneyUtils';
import { getDisplayCounterparty } from '../../utils/bankMatching';
import { formatDate as formatDateCentral } from '../../utils/formatters';
import logger from '../../utils/logger';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';

export interface TransactionsPanelProps {
  /** Bump to force a reload (e.g. after a successful import). */
  refreshToken: number;
}

const PAGE_SIZE = 25;

const matchStatusBadgeClass: Record<BankTxMatchStatus, string> = {
  unmatched: 'bg-surface-inset text-secondary border-strong',
  matched: 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] border-[var(--tone-success-border)]',
  ignored: 'bg-surface-raised text-faint border-hairline',
};

const TransactionsPanel: React.FC<TransactionsPanelProps> = ({ refreshToken }) => {
  const { t, i18n } = useTranslation('bank');

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [accountFilter, setAccountFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<BankTxMatchStatus | ''>('');

  const [txs, setTxs] = useState<BankTransaction[]>([]);
  const [cursorStack, setCursorStack] = useState<unknown[]>([]); // history of page-start cursors
  const [nextCursor, setNextCursor] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailTx, setDetailTx] = useState<BankTransaction | null>(null);
  // BR-18-C (§10.1): rawSource ist default eingeklappt; jede neu geöffnete
  // Detailansicht startet wieder eingeklappt (kein Leak des Zustands über
  // verschiedene Umsätze hinweg).
  const [showRawSource, setShowRawSource] = useState(false);

  const openDetail = (tx: BankTransaction) => {
    setShowRawSource(false);
    setDetailTx(tx);
  };

  useEffect(() => {
    listBankAccounts()
      .then(setAccounts)
      .catch(() => {
        /* Konto-Filter ist optional — ein Fehlschlag hier blockiert die Liste nicht. */
      });
  }, [refreshToken]);

  const loadPage = useCallback(
    (cursor: unknown | undefined) => {
      setLoading(true);
      setError(null);
      listTransactions({
        accountId: accountFilter || undefined,
        matchStatus: statusFilter || undefined,
        limit: PAGE_SIZE,
        cursor,
      })
        .then((result) => {
          setTxs(result.txs);
          setNextCursor(result.nextCursor);
        })
        .catch((err: any) => {
          logger.error(err, 'bank transactions load failed');
          setError(mapErrorToUserMessage(err, t, { fallbackKey: 'transactions.loadFailed' }));
        })
        .finally(() => setLoading(false));
    },
    [accountFilter, statusFilter, t]
  );

  useEffect(() => {
    setCursorStack([]);
    loadPage(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountFilter, statusFilter, refreshToken]);

  const handleNextPage = () => {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    loadPage(nextCursor);
  };

  const handlePrevPage = () => {
    if (cursorStack.length <= 1) {
      setCursorStack([]);
      loadPage(undefined);
      return;
    }
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    loadPage(newStack[newStack.length - 1]);
  };

  const formatDate = (iso: string) => formatDateCentral(iso, { locale: i18n.language });

  const formatAmount = (cents: number) =>
    centsToEuros(cents).toLocaleString(i18n.language || 'de-DE', { style: 'currency', currency: 'EUR' });

  const accountName = (accountId: string) => accounts.find((a) => a.id === accountId)?.displayName ?? accountId;

  const selectClass =
    'bg-surface-raised border border-hairline rounded-lg px-3 py-2 text-sm text-primary focus:outline-none focus:border-indigo-500';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <select className={selectClass} value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}>
          <option value="">{t('transactions.filter.allAccounts', 'Alle Konten')}</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.displayName}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as BankTxMatchStatus | '')}
        >
          <option value="">{t('transactions.filter.allStatuses', 'Alle Status')}</option>
          <option value="unmatched">{t('transactions.status.unmatched', 'offen')}</option>
          <option value="matched">{t('transactions.status.matched', 'zugeordnet')}</option>
          <option value="ignored">{t('transactions.status.ignored', 'ignoriert')}</option>
        </select>
      </div>

      {error && <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)]">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-8">
          <ClipLoader color="#6366f1" size={28} />
        </div>
      ) : txs.length === 0 ? (
        <div className="bg-surface-raised border border-hairline rounded-xl p-8 text-center text-muted">
          {t('transactions.empty', 'Keine Umsätze für diese Filter.')}
        </div>
      ) : (
        <div className="border border-hairline rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised text-muted">
              <tr>
                <th className="text-left px-3 py-2">{t('transactions.col.date', 'Datum')}</th>
                <th className="text-left px-3 py-2">{t('transactions.col.account', 'Konto')}</th>
                <th className="text-right px-3 py-2">{t('transactions.col.amount', 'Betrag')}</th>
                <th className="text-left px-3 py-2">{t('transactions.col.counterparty', 'Gegenpartei')}</th>
                <th className="text-left px-3 py-2">{t('transactions.col.purpose', 'Verwendungszweck')}</th>
                <th className="text-left px-3 py-2">{t('transactions.col.status', 'Status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border-hairline)]">
              {txs.map((tx) => (
                <tr key={tx.id} className="text-secondary">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(tx.bookingDate)}</td>
                  <td className="px-3 py-2 max-w-[140px] truncate">{accountName(tx.accountId)}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${tx.amountCents < 0 ? 'text-[var(--tone-danger-fg)]' : 'text-[var(--tone-success-fg)]'}`}>
                    {formatAmount(tx.amountCents)}
                  </td>
                  <td className="px-3 py-2 max-w-[160px] truncate">{getDisplayCounterparty(tx)}</td>
                  <td className="px-3 py-2 max-w-[220px] truncate">{tx.purpose || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${matchStatusBadgeClass[tx.matchStatus]}`}>
                      {t(`transactions.status.${tx.matchStatus}`, tx.matchStatus)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => openDetail(tx)}
                      title={t('transactions.viewDetail', 'Details')}
                      className="text-faint hover:text-primary"
                    >
                      <IconView size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handlePrevPage}
          disabled={cursorStack.length === 0 || loading}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted hover:text-primary disabled:opacity-40"
        >
          <IconChevronLeft size={10} />
          {t('transactions.prevPage', 'Zurück')}
        </button>
        <button
          type="button"
          onClick={handleNextPage}
          disabled={!nextCursor || loading}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted hover:text-primary disabled:opacity-40"
        >
          {t('transactions.nextPage', 'Weiter')}
          <IconChevronRight size={10} />
        </button>
      </div>

      {detailTx && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
          onClick={() => setDetailTx(null)}
        >
          <div
            className="bg-surface border border-hairline rounded-2xl w-full max-w-lg p-6 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-primary font-semibold">{t('transactions.detailTitle', 'Umsatz-Detail')}</h3>
            <dl className="text-sm text-secondary space-y-1.5">
              <div className="flex justify-between">
                <dt className="text-faint">{t('transactions.col.date', 'Datum')}</dt>
                <dd>{formatDate(detailTx.bookingDate)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-faint">{t('transactions.col.amount', 'Betrag')}</dt>
                <dd className={detailTx.amountCents < 0 ? 'text-[var(--tone-danger-fg)]' : 'text-[var(--tone-success-fg)]'}>
                  {formatAmount(detailTx.amountCents)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-faint">{t('transactions.col.counterparty', 'Gegenpartei')}</dt>
                <dd className="text-right">{getDisplayCounterparty(detailTx)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-faint">IBAN</dt>
                <dd>{detailTx.counterpartyIban || '—'}</dd>
              </div>
              <div>
                <dt className="text-faint mb-1">{t('transactions.col.purpose', 'Verwendungszweck')}</dt>
                <dd className="text-secondary break-words">{detailTx.purpose || '—'}</dd>
              </div>
              <div>
                {/* BR-18-C (§10.1): Rohdaten sind default eingeklappt — die
                    strukturierten Felder oben bleiben die primäre Ansicht,
                    Rohdaten bleiben für Debugging per Klick erreichbar. */}
                <button
                  type="button"
                  onClick={() => setShowRawSource((v) => !v)}
                  aria-expanded={showRawSource}
                  className="flex items-center gap-1.5 text-faint hover:text-secondary text-xs mb-1"
                >
                  {showRawSource ? <IconChevronUp size={10} /> : <IconChevronDown size={10} />}
                  {/* Bug-5-Fix (bug-analyse-2026-07-17.md): Label schärfer als „technisch,
                      für Support" statt neutralem „Rohdaten anzeigen" — macht sofort klar,
                      dass es kein regulärer Detail-Wert, sondern technischer Kontext ist. */}
                  {t('transactions.rawData.toggle', 'Technische Rohdaten (CAMT-XML, für Support)')}
                </button>
                {showRawSource && (
                  // Bug-5-Fix: monospace/Scroll-Container mit horizontalem Scroll statt
                  // Umbruch — ein rohes XML-Fragment bleibt so lesbar (Zeilen/Tags nicht
                  // hart umgebrochen), keine Funktionsänderung, reine Lesbarkeits-Politur.
                  <dd
                    data-testid="raw-source-content"
                    className="text-muted text-xs font-mono bg-surface-raised rounded-lg p-2 max-h-40 overflow-auto whitespace-pre"
                  >
                    {detailTx.rawSource}
                  </dd>
                )}
              </div>
            </dl>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setDetailTx(null)}
                className="px-4 py-2 text-sm text-muted hover:text-primary"
              >
                {t('wizard.close', 'Schließen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsPanel;
